import { useState, useContext, useEffect } from 'react';
import gql from 'graphql-tag';
import _ from 'lodash';
import MercuryContext from 'ConnectionManager/mercuryContext';
import AccountHelper from 'Workspace/helpers/AccountHelper';

const queryGetLearnerGrades = gql`
query getLearnerGrades(
  $cohortId: UUID!,
  $deploymentId: UUID!,
  $activityId: UUID!,
  $workspaceId: UUID!,
  $isWorkspace: Boolean!
) {
  cohortById(cohortId: $cohortId) {
    id
    deployment(deploymentId: $deploymentId, isWorkspace: $isWorkspace) {
      activity(activityId: $activityId) {
        id
        elementType
        configurationFields(fieldNames: ["title"]) {
          fieldName
          fieldValue
        }
        pathways {
          walkables(cohortId: $cohortId) {
            edges {
              node {
                id
                elementType
                configurationFields(fieldNames: ["title"]) {
                  fieldName
                  fieldValue
                }
              }
            }
          }
        }
      }
    }
    enrollments {
      edges {
        node {
          account {
            id
            identity {
              email
              familyName
              givenName
              honorificSuffix
              avatars(size:SMALL) {
                data
              }
            }
          }
          learnerPerformanceByDeployment(deploymentId: $deploymentId, activityId: $activityId) {
            score(cohortId: $cohortId) {
              reason
              value
            }
            progress {
              id
              completion {
                value
              }
            }
          }
        }
      }
    }
  }

  # Total scores
  workspace(workspaceId: $workspaceId) {
    id
    # find a courseware activity (argument required)
    coursewareActivity(activityId: $activityId) {
      # find pathways for this activity (all returned when argument not supplied)
      coursewarePathways {
        edges {
          node {
            id
            # find walkables for this pathway
            coursewareWalkables {
              edges {
                node {
                  id
                  # get meta info for this walkable
                  coursewareMetaInfo(keys: ["totalScore"]) {
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
`;

/**
 * Load the cohort grades data for a given activity
 * @param {string} cohortId
 * @param {string} deploymentId
 * @param {string} activityId
 * @returns {{ loading: boolean, error: Object, data: Object }}
 * A state object containing a loading, error, and the fetched data.
 */
export default function useLearnerGrades(cohortId, deploymentId, activityId, workspaceId) {
  const [state, setState] = useState({ data: {} });
  const { apolloClient } = useContext(MercuryContext);

  useEffect(() => {
    if (!activityId || !apolloClient || !cohortId || !deploymentId) {
      setState({ loading: false, error: null, data: {} });
      return;
    }

    setState({ loading: true, error: null, data: {} });
    apolloClient.query({
      query: queryGetLearnerGrades,
      fetchPolicy: 'cache-first',
      variables: {
        cohortId,
        deploymentId,
        activityId,
        workspaceId,
        isWorkspace: true,
      },
    })
      .then((response) => {
        const childWalkablesTotalScores = _.chain(response)
          .get('data.workspace.coursewareActivity.coursewarePathways.edges.0.node.coursewareWalkables.edges')
          .map(({ node }) => node)
          .map((walkableNode) => ({
            id: walkableNode.id,
            totalScore: _.get(walkableNode, 'coursewareMetaInfo.0.value'),
          }))
          .value();

        const childWalkablesMainInfo = _.chain(response)
          .get('data.cohortById.deployment.0.activity.pathways.0.walkables.edges')
          .map(({ node }) => node)
          .map((activity) => ({
            title: _.get(activity, 'configurationFields.0.fieldValue'),
            ...activity,
          }))
          .value();

        setState({
          loading: false,
          error: null,
          data: {
            workspaceId: _.chain(response)
              .get('data.cohortById.workspaceId')
              .value(),
            activity: _.chain(response)
              .get('data.cohortById.deployment.0.activity')
              .thru((activity) => activity && {
                title: _.get(activity, 'configurationFields.0.fieldValue'),
                ...activity,
              })
              .value(),
            childWalkables: _.values(
              _.merge(
                _.keyBy(childWalkablesMainInfo, 'id'),
                _.keyBy(childWalkablesTotalScores, 'id'),
              )),
            enrollments: _.chain(response)
              .get('data.cohortById.enrollments.edges')
              .map(({ node }) => node)
              .map(({
                account: { id, identity },
                learnerPerformanceByDeployment: { score, progress },
              }) => ({ ...identity, id, score, progress }))
              .sort(AccountHelper.sortListByFamilyName)
              .value(),
          },
        });
      })
      .catch((error) => setState({ loading: false, error, data: {} }));
  }, [apolloClient, workspaceId, cohortId, deploymentId, activityId]);

  return state;
}
